import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { useAuthStore } from '@/shared/auth/store';
import { parsePanelStack, serializePanelStack } from '@/shared/components/side-panel';
import { useRealtime } from '@/shared/realtime';

import {
  calendarApi,
  calendarKeys,
  patchCalendarList,
  useCalendarCounters,
  type CalendarList,
  type CalendarViewName,
} from './api';
import { calendarTopic, roomTopic } from './realtime';

const VIEWS: CalendarViewName[] = ['month', 'week', 'day', 'agenda'];
export const isCalendarView = (value: string | null): value is CalendarViewName =>
  !!value && (VIEWS as string[]).includes(value);

/** Subscribes to several realtime topics while mounted (the calendars and rooms on screen). */
export function useTopics(topics: string[]): void {
  const realtime = useRealtime();
  const key = [...topics].sort().join('|');
  useEffect(() => {
    if (!key) return undefined;
    const offs = key.split('|').map((topic) => realtime.subscribe(topic));
    return () => {
      for (const off of offs) off();
    };
  }, [realtime, key]);
}

/** Topics of the calendars and rooms of a list that other people's changes arrive on. */
export function useListTopics(
  list: CalendarList | undefined,
  calendarIds: string[],
  roomIds: string[],
): void {
  const topics = useMemo(() => {
    if (!list) return [];
    return [...calendarIds.map(calendarTopic), ...roomIds.map(roomTopic)];
  }, [list, calendarIds, roomIds]);
  useTopics(topics);
}

/** Toggle / recolor calendars and rooms with an optimistic update of the list. */
export function useListPreferences() {
  const queryClient = useQueryClient();
  const run = async (apply: (list: CalendarList) => CalendarList, request: () => Promise<unknown>) => {
    await queryClient.cancelQueries({ queryKey: calendarKeys.list });
    const rollback = patchCalendarList(queryClient, apply);
    try {
      await request();
    } catch (error) {
      rollback();
      throw error;
    } finally {
      void queryClient.invalidateQueries({ queryKey: calendarKeys.list });
    }
  };
  return {
    setCalendar: (id: string, prefs: { color?: string; isVisible?: boolean }) =>
      run(
        (list) => ({
          ...list,
          calendars: list.calendars.map((c) =>
            c.id === id
              ? { ...c, ...prefs, color: prefs.color === '' ? c.defaultColor : (prefs.color ?? c.color) }
              : c,
          ),
        }),
        () => calendarApi.calendarPreferences(id, prefs),
      ),
    setRoom: (id: string, prefs: { color?: string; isVisible?: boolean }) =>
      run(
        (list) => ({
          ...list,
          rooms: list.rooms.map((r) =>
            r.id === id
              ? { ...r, ...prefs, color: prefs.color === '' ? r.defaultColor : (prefs.color ?? r.color) }
              : r,
          ),
        }),
        () => calendarApi.roomPreferences(id, prefs),
      ),
  };
}

/** View and date of the calendar page in the URL (`?view=week&date=2026-10-19`). */
export function useCalendarLocation(defaultView: CalendarViewName, today: string) {
  const [params, setParams] = useSearchParams();
  const viewParam = params.get('view');
  const view: CalendarViewName = isCalendarView(viewParam) ? viewParam : defaultView;
  const dateParam = params.get('date');
  const date = dateParam && /^\d{4}-\d{2}-\d{2}$/u.test(dateParam) ? dateParam : today;
  const update = (changes: { view?: CalendarViewName; date?: string }, replace = false) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (changes.view) next.set('view', changes.view);
        if (changes.date) {
          if (changes.date === today) next.delete('date');
          else next.set('date', changes.date);
        }
        return next;
      },
      { replace, preventScrollReset: true },
    );
  return { view, date, update, params, setParams };
}

/**
 * Prefill parameters of the `event:new` panel: `eventAttendees`, `eventStart`, `eventCalendar` (MODULE-CONTRACTS §2)
 * plus this module's own `eventEnd`, `eventAllDay` and `eventRoom`.
 */
export const NEW_EVENT_PARAMS = [
  'eventAttendees',
  'eventStart',
  'eventEnd',
  'eventAllDay',
  'eventCalendar',
  'eventRoom',
] as const;

export interface NewEventPrefill {
  attendeeIds?: string[];
  start?: string;
  end?: string;
  allDay?: boolean;
  calendarId?: string;
  roomId?: string;
}

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

/** Reads the prefill of the `event:new` panel from a search string (malformed values are ignored). */
export function readNewEventPrefill(search: string): NewEventPrefill {
  const params = new URLSearchParams(search);
  const guid = (value: string | null) => (value && GUID.test(value) ? value : undefined);
  const instant = (value: string | null) => (value && !Number.isNaN(Date.parse(value)) ? value : undefined);
  const attendees = (params.get('eventAttendees') ?? '').split(',').filter((id) => GUID.test(id));
  return {
    attendeeIds: attendees.length > 0 ? [...new Set(attendees)] : undefined,
    start: instant(params.get('eventStart')),
    end: instant(params.get('eventEnd')),
    allDay: params.get('eventAllDay') === '1' || undefined,
    calendarId: guid(params.get('eventCalendar')),
    roomId: guid(params.get('eventRoom')),
  };
}

/** The search string of a page with the `event:new` panel opened on top (earlier prefill parameters replaced). */
export function newEventSearch(search: string, prefill: NewEventPrefill = {}): string {
  const params = new URLSearchParams(search);
  for (const key of NEW_EVENT_PARAMS) params.delete(key);
  if (prefill.attendeeIds?.length) params.set('eventAttendees', prefill.attendeeIds.join(','));
  if (prefill.start) params.set('eventStart', prefill.start);
  if (prefill.end) params.set('eventEnd', prefill.end);
  if (prefill.allDay) params.set('eventAllDay', '1');
  if (prefill.calendarId) params.set('eventCalendar', prefill.calendarId);
  if (prefill.roomId) params.set('eventRoom', prefill.roomId);
  const stack = parsePanelStack(search).filter((entry) => entry.key !== 'event:new');
  return serializePanelStack(params.toString(), [...stack, { type: 'event', id: 'new' }]);
}

/** Opens the new-event panel on top of the current panels, optionally prefilled. */
export function useOpenNewEvent() {
  const location = useLocation();
  const navigate = useNavigate();
  return useCallback(
    (prefill: NewEventPrefill = {}) => {
      const stack = parsePanelStack(location.search).filter((entry) => entry.key !== 'event:new');
      // Same history marker as useSidePanel().open: closing the panel goes back to this page.
      void navigate(
        { pathname: location.pathname, search: newEventSearch(location.search, prefill) },
        { state: { sidePanelPushed: stack.length + 1 }, preventScrollReset: true },
      );
    },
    [location.pathname, location.search, navigate],
  );
}

/** Sidebar badge: invitations waiting for an answer (kept live by `calendar.invitations-changed`). */
export function useCalendarNavCounter(): number | undefined {
  const signedIn = useAuthStore((s) => s.status === 'authenticated');
  const count = useCalendarCounters({ enabled: signedIn }).data?.invitations ?? 0;
  return count > 0 ? count : undefined;
}
